/**
 * ComplianceSetupGate — blocks app access until the org owner confirms
 * compliance settings.
 *
 * F-001: Compliance Gate
 *
 * Reads `complianceConfirmed` from /api/v1/consent/status (useConsentStatus).
 * Confirmed → renders children. Otherwise the branch depends on who is looking:
 *
 * - **Platform operator** (`isOwner` or globalRole `admin`) — passes through.
 *   They must not confirm on the client's behalf: the confirmation is
 *   idempotent forever, so the real owner could never record themselves as the
 *   actor. They get a non-blocking banner on the dashboard instead.
 * - **Org owner** (`canManageUsers`, i.e. globalRole `client` here) —
 *   full-screen setup form.
 * - **Everyone else** — a BLOCKING informational screen, not a pass-through:
 *   they cannot confirm, and rendering the app would hide the fact that ingest
 *   is dropping every record meanwhile. In practice this branch is rarely
 *   reached — useConsentStatus resolves fail-open (`complianceConfirmed: true`)
 *   without a request for the roles RBAC would 403 anyway (agent, supervisor),
 *   so they leave through the "confirmed" branch above.
 * - **Withdrawn / unreadable settings** — only the Settings surfaces needed
 *   to withdraw, disconnect, revoke keys, repair settings, or reconfirm remain
 *   reachable. Every other route gets a remediation-only screen.
 *
 * Both non-passing screens carry GateEscapeBar (org switch + sign out): the
 * gate renders INSTEAD of AppShell, so without it the screen is a dead end.
 * While the status is loading — a layout skeleton. On a fetch error
 * useConsentStatus fails open, so the app still renders.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { SignOutButton } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageToggle } from '@/components/theme/LanguageToggle';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { usePermissions, useRole } from '@/contexts/RoleContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { AppLayoutSkeleton } from '@/components/ui/skeletons';
import { logError } from '@/lib/logger';
import { useConsentStatus, consentStatusQueryKey } from './useConsentStatus';

interface ComplianceSetupGateProps {
  children: React.ReactNode;
}

const COMPLIANCE_REMEDIATION_SECTIONS = new Set([
  'compliance_settings',
  'consent',
  'data_source',
  'api_keys',
]);

function isComplianceRemediationRoute(pathname: string, search: unknown): boolean {
  if (pathname !== '/settings' && pathname !== '/settings/') return false;
  if (!search || typeof search !== 'object') return false;
  const section = (search as Record<string, unknown>).section;
  return typeof section === 'string' && COMPLIANCE_REMEDIATION_SECTIONS.has(section);
}

const CONSENT_MODE_OPTIONS = [
  {
    value: 'all_party',
    labelKey: 'compliance.allParty',
    descriptionKey: 'compliance.allPartyDesc',
    fallback: 'Consent from all participants',
  },
  {
    value: 'one_party',
    labelKey: 'compliance.oneParty',
    descriptionKey: 'compliance.onePartyDesc',
    fallback: 'Consent from one participant (where permitted)',
  },
  {
    value: 'employer_notification',
    labelKey: 'compliance.employerNotification',
    descriptionKey: 'compliance.employerNotificationDesc',
    fallback: 'Employee notification (staff only)',
  },
  {
    value: 'other_lawful_basis',
    labelKey: 'compliance.otherLawfulBasis',
    descriptionKey: 'compliance.otherLawfulBasisDesc',
    fallback: 'Other lawful basis (participant consent not relied upon)',
  },
] as const;

const JURISDICTION_OPTIONS = [
  { value: 'ua', label: 'Ukraine' },
  { value: 'eu_strict', label: 'EU — Strict (DE, FR, IT, AT, BE)' },
  { value: 'eu_moderate', label: 'EU — Moderate (NL, SE, DK, FI, IE)' },
  { value: 'us_all_party', label: 'US — All-party states (CA, FL, IL)' },
  { value: 'us_one_party', label: 'US — One-party states (TX, NY)' },
  { value: 'custom', label: 'Custom' },
];

const LEGAL_BASIS_OPTIONS = [
  {
    value: 'consent',
    labelKey: 'compliance.legalBasisOptions.consent',
    fallback: 'Consent',
  },
  {
    value: 'legitimate_interest',
    labelKey: 'compliance.legalBasisOptions.legitimateInterest',
    fallback: 'Legitimate interests',
  },
  {
    value: 'contract',
    labelKey: 'compliance.legalBasisOptions.contract',
    fallback: 'Performance of a contract',
  },
  {
    value: 'legal_obligation',
    labelKey: 'compliance.legalBasisOptions.legalObligation',
    fallback: 'Compliance with a legal obligation',
  },
  {
    value: 'other_applicable_law',
    labelKey: 'compliance.legalBasisOptions.otherApplicableLaw',
    fallback: 'Other basis under applicable law',
  },
] as const;

/**
 * Выход из гейта. Гейт рендерится ВМЕСТО AppShell, где живут переключатель
 * организаций и меню пользователя, — без этой шапки экран является тупиком:
 * пользователь с доступом к нескольким организациям не может уйти в ту,
 * где всё в порядке, а сотрудник без права подтверждения — вообще никуда.
 */
function GateEscapeBar({
  organizations,
  selectedOrgId,
  onSelectOrganization,
}: {
  organizations: Array<{ id: string; name: string }>;
  selectedOrgId: string | null;
  onSelectOrganization: (orgId: string) => void;
}) {
  const { t } = useTranslation(['settings', 'auth', 'common']);

  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      {organizations.length > 1 ? (
        <Select
          data-testid="compliance-org-switch"
          value={selectedOrgId ?? ''}
          onValueChange={(val) => onSelectOrganization(val as string)}
          options={organizations.map((org) => ({ value: org.id, label: org.name }))}
          aria-label={t('compliance.switchOrganization', 'Switch organization')}
          size="sm"
          className="w-full shrink-0 sm:w-56"
        />
      ) : (
        <span />
      )}

      <div className="ml-auto flex items-center gap-2">
        <LanguageToggle showLabel />
        <SignOutButton redirectUrl="/sign-in">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="compliance-sign-out"
          >
            {t('auth:auth.signOut', 'Sign out')}
          </Button>
        </SignOutButton>
      </div>
    </div>
  );
}

function ComplianceRemediationScreen({
  settingsUnreadable,
  organizations,
  selectedOrgId,
  onSelectOrganization,
}: {
  settingsUnreadable: boolean;
  organizations: Array<{ id: string; name: string }>;
  selectedOrgId: string | null;
  onSelectOrganization: (orgId: string) => void;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const links = [
    {
      href: '/settings?section=compliance_settings',
      label: t('compliance.remediationCompliance', 'Review and reconfirm'),
    },
    {
      href: '/settings?section=consent',
      label: t('compliance.remediationConsent', 'Consent status'),
    },
    {
      href: '/settings?section=data_source',
      label: t('compliance.remediationSource', 'Disconnect data source'),
    },
    {
      href: '/settings?section=api_keys',
      label: t('compliance.remediationApiKeys', 'Revoke API keys'),
    },
  ];

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-void p-4">
      <div className="w-full max-w-lg space-y-6">
        <GateEscapeBar
          organizations={organizations}
          selectedOrgId={selectedOrgId}
          onSelectOrganization={onSelectOrganization}
        />
        <div
          className="space-y-5 rounded-card border border-warning/40 bg-bg-elevated p-6"
          data-testid="compliance-remediation"
        >
          <div className="space-y-3 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-warning" />
            <h2 className="text-xl font-semibold text-fg">
              {t('compliance.remediationTitle', 'Compliance action required')}
            </h2>
            <p className="text-sm text-fg-muted">
              {settingsUnreadable
                ? t(
                    'compliance.remediationUnreadable',
                    'Compliance settings cannot be read. Processing remains fail-open, so only the controls needed to repair settings or stop intake are available.'
                  )
                : t(
                    'compliance.remediationWithdrawn',
                    'Recording consent was withdrawn. Only the controls needed to reconfirm compliance or stop intake are available.'
                  )}
            </p>
          </div>
          <nav
            aria-label={t('compliance.remediationActions', 'Compliance actions')}
            className="grid gap-2 sm:grid-cols-2"
          >
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-button border border-border bg-bg px-3 py-2 text-center text-sm font-medium text-primary-fg transition-colors hover:border-primary hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

export function ComplianceSetupGate({ children }: ComplianceSetupGateProps) {
  const { t } = useTranslation(['settings', 'common']);
  const { authenticatedFetch } = useAuthFetch();
  const queryClient = useQueryClient();

  const { isOwner, organizations, selectOrganization } = useOrganization();
  const { canManageUsers } = usePermissions();
  const { selectedOrgId, globalRole } = useRole();
  const location = useLocation();

  // Платформенные роли НЕ подтверждают compliance за клиента: подтверждение
  // идемпотентно навсегда (consent.ts:196 → already_confirmed), и настоящий
  // владелец организации после этого не сможет записать себя как актора.
  // Им показывается неблокирующий баннер на дашборде (dashboard.tsx).
  const isPlatformOperator = isOwner || globalRole === 'admin';
  // Подтвердить может только владелец организации.
  const canConfirmCompliance = !isPlatformOperator && canManageUsers;

  const { data: status, isLoading, refetch } = useConsentStatus();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Require the owner to make each legally meaningful choice explicitly.
  const [consentMode, setConsentMode] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [legalBasis, setLegalBasis] = useState('');
  const [accepted, setAccepted] = useState(false);
  const isComplete = Boolean(consentMode && jurisdiction && legalBasis);

  const handleConfirm = async () => {
    if (!accepted || !isComplete) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await authenticatedFetch('/api/v1/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          consentConfig: { consentMode, jurisdiction, legalBasis },
        }),
      });

      await authenticatedFetch('/api/v1/consent/confirm-compliance', {
        method: 'POST',
      });

      queryClient.setQueryData(consentStatusQueryKey(selectedOrgId), {
        ...(status ?? { terms_of_service: true, privacy_policy: true }),
        complianceConfirmed: true,
      });
      void refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('common:common.unknownError', 'An unknown error occurred');
      setError(message);
      logError('compliance.confirm_failed', {
        component: 'ComplianceSetupGate',
        error: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Defensive skeleton: MainAppLayout gates the splash on the same query, so
  // by the time this renders `status` is usually populated — but fall back to
  // a skeleton instead of a blank frame on stale-cache refetch or org switch.
  if (isLoading || !status) {
    return <AppLayoutSkeleton />;
  }

  if (status.complianceConfirmed) {
    return <>{children}</>;
  }

  // Платформенный оператор в чужой организации — пропускаем, статус он видит
  // баннером на дашборде.
  if (isPlatformOperator) {
    return <>{children}</>;
  }

  // Deliberate withdrawal and unreadable settings are not the initial setup
  // state: the client must be able to withdraw/reconfirm, disconnect sources,
  // or revoke API keys. This gate wraps the root Outlet, though, so passing all
  // children here would also reopen records, dashboards and every other route
  // while intake is fail-open. Admit only those explicit remediation sections.
  //
  // Только тому, кто МОЖЕТ это сделать. Экран ремедиации состоит из действий —
  // отключить источник, отозвать ключи, переподтвердить, — и все они закрыты
  // теми же правами, что и подтверждение. Участнику без них он показывал
  // четыре ссылки, ведущие в отказ; ниже для него стоит информационный экран,
  // написанный ровно про это состояние.
  if (
    canConfirmCompliance &&
    (status.recordingWithdrawn || status.complianceSettingsUnreadable)
  ) {
    if (isComplianceRemediationRoute(location.pathname, location.search)) {
      return <>{children}</>;
    }

    return (
      <ComplianceRemediationScreen
        settingsUnreadable={status.complianceSettingsUnreadable}
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        onSelectOrganization={selectOrganization}
      />
    );
  }

  // Non-admin: show informational screen (can't confirm, so don't show form)
  if (!canConfirmCompliance) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-void p-4">
        <div className="max-w-lg w-full space-y-4">
          <GateEscapeBar
            organizations={organizations}
            selectedOrgId={selectedOrgId}
            onSelectOrganization={selectOrganization}
          />
          <div className="text-center space-y-4">
            <ShieldCheck className="h-8 w-8 text-warning mx-auto" />
            <h2 className="text-xl font-semibold text-fg">
              {t('compliance.onboardingTitle', 'Compliance Setup')}
            </h2>
            <p className="text-fg-muted text-sm">
              {t(
                'compliance.notConfirmedWarning',
                'Recording processing is paused until an admin confirms compliance settings.'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Admin: show compliance setup form
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-void p-4">
      <div className="max-w-lg w-full">
        <GateEscapeBar
          organizations={organizations}
          selectedOrgId={selectedOrgId}
          onSelectOrganization={selectOrganization}
        />

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-dim mb-4">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold text-fg mb-2">
            {t('compliance.onboardingTitle', 'Compliance Setup')}
          </h2>
          <p className="text-fg-muted text-sm">
            {t(
              'compliance.onboardingDescription',
              'Before Aelo can process recordings, provide your organization’s recording approach, jurisdiction, and legal basis.'
            )}
          </p>
        </div>

        {/* Form */}
        <div className="space-y-5 mb-6">
          {/* Consent Mode */}
          <div className="space-y-1.5">
            <label
              htmlFor="compliance-consent-mode"
              className="text-sm font-medium text-fg"
            >
              {t('compliance.consentMode', 'Recording and notification approach')}
            </label>
            <Select
              id="compliance-consent-mode"
              data-testid="compliance-consent-mode"
              value={consentMode}
              onValueChange={(val) => setConsentMode(val as string)}
              disabled={isSubmitting}
              className="w-full"
              placeholder={t('compliance.selectConsentMode', 'Select an approach')}
              options={CONSENT_MODE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey, opt.fallback),
                description: t(opt.descriptionKey),
              }))}
            />
            <p className="text-xs text-fg-muted">
              {t(
                'compliance.consentModeStatutoryNote',
                'These are operational descriptions, not terms from a specific statute. Choose the option that matches your organization’s practice.'
              )}
            </p>
          </div>

          {/* Jurisdiction */}
          <div className="space-y-1.5">
            <label
              htmlFor="compliance-jurisdiction"
              className="text-sm font-medium text-fg"
            >
              {t('compliance.jurisdiction', 'Jurisdiction')}
            </label>
            <Select
              id="compliance-jurisdiction"
              data-testid="compliance-jurisdiction"
              value={jurisdiction}
              onValueChange={(val) => setJurisdiction(val as string)}
              disabled={isSubmitting}
              className="w-full"
              placeholder={t('compliance.selectJurisdiction', 'Select a jurisdiction')}
              options={JURISDICTION_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(
                  `compliance.jurisdictionPresets.${opt.value === 'eu_strict' ? 'euStrict' : opt.value === 'eu_moderate' ? 'euModerate' : opt.value === 'us_all_party' ? 'usAllParty' : opt.value === 'us_one_party' ? 'usOneParty' : opt.value}`,
                  opt.label
                ),
              }))}
            />
          </div>

          {/* Legal Basis */}
          <div className="space-y-1.5">
            <label
              htmlFor="compliance-legal-basis"
              className="text-sm font-medium text-fg"
            >
              {t('compliance.legalBasis', 'Legal Basis')}
            </label>
            <Select
              id="compliance-legal-basis"
              data-testid="compliance-legal-basis"
              value={legalBasis}
              onValueChange={(val) => setLegalBasis(val as string)}
              disabled={isSubmitting}
              className="w-full"
              placeholder={t('compliance.selectLegalBasis', 'Select a legal basis')}
              options={LEGAL_BASIS_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey, opt.fallback),
              }))}
            />
            <p className="text-xs text-fg-muted">
              {t(
                'compliance.legalBasisHelp',
                'Choose the basis that applies to your organization under the selected jurisdiction.'
              )}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="compliance-responsibility"
              data-testid="compliance-responsibility"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
            />
            <label
              htmlFor="compliance-responsibility"
              className="cursor-pointer text-sm text-fg"
            >
              {t(
                'compliance.controllerAcknowledgement',
                'I understand that my organization, as controller, is responsible for determining and documenting an applicable legal basis and for providing notices or obtaining consent where required by applicable law.'
              )}
            </label>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 rounded-lg bg-danger-dim border border-danger mb-4">
            <p className="text-sm text-danger-fg">{error}</p>
          </div>
        )}

        {/* CTA */}
        <Button
          onClick={handleConfirm}
          disabled={!accepted || !isComplete || isSubmitting}
          size="lg"
          data-testid="compliance-confirm"
          className="w-full gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('common:common.saving', 'Saving...')}
            </>
          ) : (
            t('compliance.onboardingConfirm', 'Confirm & Continue')
          )}
        </Button>
      </div>
    </div>
  );
}
